import { Alert, Card } from '@mui/material';
import { LoadingSpinner } from 'components/data/LoadingSpinner';
import { differenceInMonths, isAfter, subYears } from 'date-fns';
import { PointOptionsType, SeriesLineOptions } from 'highcharts';
import { useUserSession } from 'hooks';
import { useEffect, useState } from 'react';
import { useLazyGetTrendMonthDataQuery } from 'store/services/efficiency';
import { EfficiencyGraph } from 'views/Efficiency/tabs/EfficiencyTrend/components/EfficiencyGraph/EfficiencyGraph';
import { TrendData } from 'views/Efficiency/tabs/EfficiencyTrend/components/TrendData/TrendData';
import styles from '../../Efficiency.module.scss';
import { TrendSummaryBanner } from './components/TrendSummaryBanner/TrendSummaryBanner';
import { isArray } from 'lodash';

interface EfficiencyTrendProps {
    supplierId?: string;
}

export const EfficiencyTrend = ({ supplierId }: EfficiencyTrendProps) => {
    const [getTrendData, { data, isLoading, isFetching }] =
        useLazyGetTrendMonthDataQuery();
    const session = useUserSession();
    const [deliverySizeTarget, setDeliverySizeTarget] = useState(
        session?.efficiencyTrend?.deliverySizeTarget ?? 0
    );
    const [minDate, setMinDate] = useState<Date>(subYears(new Date(), 1));
    const [series, setSeries] = useState<SeriesLineOptions[]>([]);
    const [monthDiff, setMonthDiff] = useState(
        differenceInMonths(new Date(), minDate)
    );

    // Load trend data.
    useEffect(() => {
        if (supplierId !== undefined && !data) {
            getTrendData({
                supplierId,
            });
        }
    }, [getTrendData, supplierId, isLoading, isFetching, data]);

    useEffect(() => {
        if (series?.[0]?.data && minDate) {
            const newMonthDiff = series[0].data.reduce(
                (sum: number, point: PointOptionsType) => {
                    if (!point || !isArray(point) || !point[0]) {
                        return sum;
                    }

                    const pointDate = new Date(point[0]);
                    return isAfter(pointDate, minDate) ? sum + 1 : sum;
                },
                0
            );

            if (newMonthDiff > 0) {
                setMonthDiff(newMonthDiff);
            }
        }
    }, [series, minDate]);

    // Construct trend graph.
    useEffect(() => {
        if (data) {
            const tempSeries: SeriesLineOptions[] = [
                {
                    type: 'line',
                    color: '#ff7f0e',
                    name: 'Delivery Count',
                    yAxis: 0,
                    tooltip: {
                        valueDecimals: 1,
                        xDateFormat: '%B %Y', // Month Year in "August 2023" format.
                    },
                    dataGrouping: {
                        approximation: 'sum',
                        enabled: true,
                        forced: true,
                        units: [['month', [1]]],
                    },
                },
                {
                    type: 'line',
                    color: '#1f77b4',
                    name: 'Avg. Delivery Size',
                    yAxis: 1,
                    tooltip: {
                        valueSuffix: '%',
                        xDateFormat: '%B %Y', // Month Year in "August 2023" format.
                    },
                    dataGrouping: {
                        approximation: 'sum',
                        enabled: true,
                        forced: true,
                        units: [['month', [1]]],
                    },
                },
            ];

            tempSeries[0].data = data.counts;
            tempSeries[1].data = data.averages;

            setSeries(tempSeries);
        }
    }, [data, deliverySizeTarget]);

    return (
        <div data-testid="EfficiencyTrend">
            {!isLoading && !isFetching && !data ? (
                <Alert
                    className={styles.noDataAlert}
                    title="No efficiency data found"
                    severity="error"
                >
                    No efficiency data found for user supplier
                </Alert>
            ) : (
                <>
                    {isLoading || isFetching ? (
                        <div className={styles.graphLoading}>
                            <LoadingSpinner />
                        </div>
                    ) : (
                        <Card className={styles.graphCard}>
                            <TrendSummaryBanner minDate={minDate} />
                        </Card>
                    )}
                    <Card className={styles.graphCard}>
                        {isLoading || isFetching ? (
                            <div className={styles.graphLoading}>
                                <LoadingSpinner />
                            </div>
                        ) : (
                            <EfficiencyGraph
                                deliverySizeTarget={deliverySizeTarget}
                                setMinDate={setMinDate}
                                series={series}
                            />
                        )}
                    </Card>
                    {(supplierId || supplierId?.toString() === '0') && (
                        <TrendData
                            minDate={minDate}
                            deliverySizeTarget={deliverySizeTarget}
                            setDeliverySizeTarget={setDeliverySizeTarget}
                            supplierId={supplierId}
                            monthDiff={monthDiff}
                        />
                    )}
                </>
            )}
        </div>
    );
};
